/**
 * Status de conexao WhatsApp que valem como OFFLINE para avisar o atendente.
 *
 * 🔴 A LISTA E' MENOR QUE A DO UPSTREAM, DE PROPOSITO. Eles incluem tambem
 * `OPENING`, `PAIRING` e `qrcode`. Medido no nosso backend: `OPENING` e' gravado
 * em DEZ lugares como estado NORMAL e transitorio — todo `StartWhatsAppSession`,
 * todo `StartWuzAPISession`, toda criacao e clonagem de conexao, e o
 * `CheckCompanyConnectionsService` o lista como status ATIVO.
 *
 * Ou seja: adotar a lista deles faria o aviso aparecer para TODOS os atendentes
 * a cada reinicio do backend, dizendo que a conexao caiu quando ela esta apenas
 * subindo. Aviso que aparece a toa e' pior que aviso nenhum — o atendente aprende
 * a ignorar, e ai o aviso de verdade tambem passa batido.
 *
 * Ficam so' os estados que significam "a mensagem NAO vai sair e ninguem vai
 * consertar sozinho": DISCONNECTED, TIMEOUT e qrcode (este entrou depois — ver
 * o bloco abaixo).
 */
export const OFFLINE_WHATSAPP_STATUSES = ["DISCONNECTED", "TIMEOUT", "qrcode"];

/**
 * 🔴 `qrcode` ENTROU depois da revisao adversarial, e ele e' o mais importante
 * dos tres. Quando o telefone do cliente desloga, a conexao vai para `qrcode` —
 * "esperando alguem escanear" — e FICA ALI horas ou dias. Ha ate um cron
 * (`wuzapiQrAutoRefresh`) que renova o codigo e MANTEM esse status.
 * Deixa-lo de fora era o pior dos mundos: o aviso aparecia so' nos estados curtos
 * e ficava mudo justamente no que dura o dia inteiro — que e' quando o atendente
 * chega de manha, digita e a mensagem nao sai.
 *
 * `OPENING` e `PAIRING` continuam FORA, e isso segue certo: `OPENING` e' gravado
 * em DEZ lugares do backend como estado normal de quem esta subindo (todo
 * restart), e avisar ali faria a faixa aparecer para a frota inteira a cada
 * reinicio, treinando o atendente a ignorar o aviso.
 */

/**
 * Texto proprio por status: "caiu" e "esperando QR" sao problemas diferentes.
 * O atendente le isto na tela: nada de status cru em ingles ("esta DISCONNECTED").
 */
export const describeOfflineStatus = (status, name) => {
  const s = String(status || "").trim().toUpperCase();
  const conexao = name || "WhatsApp";
  if (s === "QRCODE") {
    return `Conexão ${conexao} está aguardando a leitura do QR Code.`;
  }
  if (s === "DISCONNECTED") {
    return `Conexão ${conexao} está desconectada.`;
  }
  if (s === "TIMEOUT") {
    return `Conexão ${conexao} parou de responder.`;
  }
  return `Conexão ${conexao} está fora do ar (${status}).`;
};

export const isWhatsappOffline = status => {
  if (status == null || status === "") return false;
  const normalized = String(status).trim().toUpperCase();
  return OFFLINE_WHATSAPP_STATUSES.some(s => s.toUpperCase() === normalized);
};

/**
 * Status/nome da conexao de um ticket, preferindo a lista VIVA (WhatsAppsContext),
 * que chega por socket, e caindo no que veio junto do ticket.
 *
 * A ordem importa: o objeto do ticket foi carregado quando o ticket abriu e pode
 * estar velho. A lista viva reflete a queda que aconteceu ha 10 segundos.
 */
export const resolveTicketWhatsappConnection = (ticket, whatsApps = []) => {
  const whatsappId = ticket?.whatsappId ?? ticket?.whatsapp?.id ?? null;
  const fromTicket = ticket?.whatsapp || null;
  const fromList =
    whatsappId != null && Array.isArray(whatsApps)
      ? whatsApps.find(w => Number(w?.id) === Number(whatsappId))
      : null;

  const status = fromList?.status ?? fromTicket?.status ?? null;
  const name = fromList?.name || fromTicket?.name || "WhatsApp";

  return { whatsappId, name, status, offline: isWhatsappOffline(status) };
};

/** Versao por id, para quem tem so' o `whatsappId` (ex.: o MessageInput). */
export const resolveWhatsappConnectionById = (whatsappId, whatsApps = []) => {
  const found =
    whatsappId != null && Array.isArray(whatsApps)
      ? whatsApps.find(w => Number(w?.id) === Number(whatsappId))
      : null;
  const status = found?.status ?? null;
  return {
    whatsappId,
    name: found?.name || "WhatsApp",
    status,
    offline: isWhatsappOffline(status)
  };
};

export default isWhatsappOffline;
